import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo/Logo.svg";
import Carousel from "../carousel/logincarousel";
import loginbanner from "../../assets/images/login-banner.png";
import loginbannertwo from "../../assets/images/login-banner-2.png";
import loginbannerthree from "../../assets/images/login-banner-3.png";
import loginbannerfour from "../../assets/images/login-banner-4.png";
import loginbannersm from "../../assets/images/login-banner-sm.png";
import bannerlayout from "../../assets/images/banner-layout.png";
import { formatDateOnly } from "../../utils/date";
import { CmsHtml, useCmsPage } from "./cmsContent";

const slides = [loginbanner, loginbannertwo, loginbannerthree, loginbannerfour];
const slidesSm = [loginbannersm, loginbannersm, loginbannersm, loginbannersm];

export const LegalBackButton = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      // Opened directly (no history in this tab) there is nowhere to go back to
      onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
      className="mt-5 h-[56px] w-full rounded-xl bg-[#161F28] hover:bg-[#1B2537]/90 text-white font-medium"
    >
      Back
    </button>
  );
};

// Signup-styled page for a CMS legal document; `renderActions` gets readToEnd (scrolled to the end, or short enough to fit)
export default function LegalPage({ slug, title, subtitle, cardLabel, renderActions }) {
  const { status, page, reload } = useCmsPage(slug);
  const [readToEnd, setReadToEnd] = useState(false);
  const contentRef = useRef(null);

  const checkReadToEnd = useCallback(() => {
    const el = contentRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
  }, []);

  useEffect(() => {
    if (status === "ready") checkReadToEnd();
  }, [status, checkReadToEnd]);

  return (
    <div className="login w-full">
      <div className="container mx-auto flex">
        <div className="hidden lg:block lg:w-1/2">
          <div className="carousel-size pb-3 pt-3">
            <Carousel slides={slides} />
            <img src={bannerlayout} alt="" className="layout" />
          </div>
          <div className="copy-rights py-6 text-center text-sm text-[#095228]">
            © 2025 Nyawoo. All Rights Reserved
          </div>
        </div>

        <div className="w-full lg:w-1/2 login-content flex flex-col">
          <div className="carousel-d-none-sm md:block lg:hidden">
            <div className="carousel-size pb-3 pt-3">
              <Carousel slides={slides} />
              <img src={bannerlayout} alt="" className="layout" />
            </div>
          </div>
          <div className="lg:hidden md:hidden">
            <div className="carousel-size pb-3 pt-3">
              <Carousel slides={slidesSm} />
              <img src={bannerlayout} alt="" className="layout" />
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center px-4 sm:px-6 py-6 lg:py-10">
            <div className="w-full max-w-[560px] flex flex-col">
              <div className="mb-6 flex justify-center">
                <img src={logo} alt="Nyawoo Logo" width={150} height={50} />
              </div>
              <h1 className="text-3xl font-semibold text-center text-[#161F28]">{title}</h1>
              <p className="text-center text-gray-600 mt-2 mb-6">{subtitle}</p>

              {(status === "idle" || status === "loading") && (
                <div role="status" className="rounded-2xl border border-[#D7E7DC] bg-[#F5FAF6] p-6 text-center text-gray-600">
                  Loading the {title}...
                </div>
              )}

              {status === "error" && (
                <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 text-center">
                  <p>We couldn't load the {title} right now.</p>
                  <button
                    type="button"
                    className="mt-3 px-5 py-2 rounded-xl border border-red-300 text-red-700 hover:bg-red-100"
                    onClick={reload}
                  >
                    Try again
                  </button>
                </div>
              )}

              {status === "ready" && (
                <>
                  <div className="rounded-2xl border border-[#D7E7DC] bg-white shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-[#EBF8ED] border-b border-[#D7E7DC] text-sm font-semibold text-[#095228]">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#39B54A]" aria-hidden="true" />
                      {cardLabel}
                      {page.updated_at && (
                        <span className="ml-auto text-xs font-normal text-gray-500">
                          Last updated {formatDateOnly(page.updated_at)}
                        </span>
                      )}
                    </div>
                    <CmsHtml
                      ref={contentRef}
                      html={page.content}
                      onScroll={checkReadToEnd}
                      tabIndex={0}
                      className="max-h-[52vh] overflow-y-auto px-5 py-4 focus:outline-none"
                    />
                  </div>
                  {renderActions({ readToEnd })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
